import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterIcon } from '../../assets';
import {
  ACTION_TYPE_OPTIONS,
  CAMPAIGN_RECURRENT_STATUS,
  CAMPAIGN_STATUS,
  CAMPAIGN_USER_STATUS,
  PLATFORMS,
} from '../../config/constants';
import { usePermissions } from '../../hooks/usePermissions';
import type { CampaignFilters } from '../../types';
import { Button } from '../common/Button';
import { GenericDropdown } from '../inputs/GenericDropdown';
import { MoneyInput } from '../inputs/MoneyInput';
import MonthDateFromTo from '../inputs/MonthDateFromTo';
import { UserDropdown } from '../inputs/UserDropdown';
import { FiltersPopover } from './FiltersPopover';

type CampaignFiltersProps = {
  setFilters: (val: CampaignFilters) => void;
  filters: CampaignFilters;
  recurrent?: boolean;
};

export const CampaignFiltersPopover = ({
  setFilters,
  filters,
  recurrent = false,
}: CampaignFiltersProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const { ...methods } = useForm<CampaignFilters>({
    defaultValues: { ...filters },
  });

  const { control, handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    reset(filters);
  }, [filters]);

  // The `influencers`/`client` dropdowns use `storeFullOption`, which stores
  // the entire react-select option (including extra UI-only fields like
  // `exclusivity`). The GraphQL `influencerFilter` input type only accepts
  // `value`, `label` and `image`, so sending the raw option makes the query
  // fail with a variable validation error (and the table gets stuck loading
  // forever, since `onCompleted` never runs). Strip it down to the allowed shape.
  const sanitizeInfluencerFilter = (
    values?: { value: string; label: string; image?: string }[],
  ) => values?.map(({ value, label, image }) => ({ value, label, image }));

  const handleSearch = (data: CampaignFilters) => {
    setFilters({
      ...data,
      influencers: sanitizeInfluencerFilter(data.influencers as any),
      client: sanitizeInfluencerFilter(data.client as any),
      minPrice: data.minPrice !== undefined ? Number(data.minPrice) : undefined,
      maxPrice: data.maxPrice !== undefined ? Number(data.maxPrice) : undefined,
    });
    setOpen(false);
  };

  const onReset = () => {
    setFilters({});
    reset({});
  };
  const { isInfluencer, isBrand, isInfluentia } = usePermissions();
  let options = [];

  if (isInfluencer()) {
    options = CAMPAIGN_USER_STATUS.filter(
      (status) => status !== 'not_send',
    ).map((s) => ({
      value: s,
      label: t(`influencerStatus.${s}`),
    }));
  } else if (isBrand()) {
    options = CAMPAIGN_STATUS.filter((status) =>
      ['active', 'finished', 'canceled'].includes(status),
    ).map((s) => ({
      value: s,
      label: t(`campaignStatus.${s}`),
    }));
  } else if (recurrent) {
    options = CAMPAIGN_RECURRENT_STATUS.map((s) => ({
      value: s,
      label: t(`campaignStatus.${s}`),
    }));
  } else {
    options = CAMPAIGN_STATUS.map((s) => ({
      value: s,
      label: t(`campaignStatus.${s}`),
    }));
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style="light-blue"
        text=""
        iconLeft={<FilterIcon />}
      />
      {open && (
        <FiltersPopover
          title={t('campaigns.filtersTitle')}
          closeModal={() => setOpen(false)}
          resetFilter={onReset}
          onSubmit={handleSubmit(handleSearch)}
        >
          <FormProvider {...methods}>
            <form
              className="flex flex-col gap-y-5"
              onSubmit={handleSubmit(handleSearch)}
            >
              <GenericDropdown
                isMulti
                control={control}
                name="status"
                label={t('campaigns.statusFilter')}
                placeholder={t('campaigns.statusFilterPlaceholder')}
                options={options}
              />
              {isInfluentia() && (
                <>
                  <UserDropdown
                    type="influencer"
                    label={t('campaigns.influencerLabelFilter')}
                    name="influencers"
                    multi={true}
                    control={control}
                    searchable
                    storeFullOption={true}
                  />
                  <UserDropdown
                    multi={true}
                    control={control}
                    storeFullOption={true}
                    searchable
                    name="client"
                    label={t('campaigns.createClient')}
                    placeholder={t('campaigns.createClientPlaceholder')}
                    type="brand"
                  />
                </>
              )}
              <GenericDropdown
                isMulti
                control={control}
                name="platforms"
                label={t('campaigns.platformsFilter')}
                placeholder={t('campaigns.platformsFilterPlaceholder')}
                options={[
                  ...PLATFORMS.map((p) => ({
                    value: p,
                    label: p,
                  })),
                ]}
              />
              <GenericDropdown
                isMulti
                control={control}
                name="actionTypes"
                label={t('campaigns.actionTypesFilter')}
                placeholder={t('campaigns.actionTypesFilterPlaceholder')}
                options={ACTION_TYPE_OPTIONS.map((v) => ({
                  value: v,
                  label: t(`actionType.${v}`),
                })).sort((a, b) => a.label.localeCompare(b.label))}
              />
              <div className="flex flex-row gap-x-2">
                <MoneyInput
                  label={t('campaigns.princeRangeMinFilter')}
                  placeholder={t('campaigns.princeRangeMinFilterPlaceholder')}
                  control={control}
                  name={'minPrice'}
                />
                <MoneyInput
                  label={t('campaigns.princeRangeMaxFilter')}
                  placeholder={t('campaigns.princeRangeMaxFilterPlaceholder')}
                  control={control}
                  name={'maxPrice'}
                />
              </div>
              <div>
                <p className="my-2 block text-[14px] font-[600]">
                  {t('campaigns.campaignMonth')}
                </p>
                <MonthDateFromTo
                  onChangeFunction={(month_from, month_to) => {
                    setValue('from_month', month_from);
                    setValue('to_month', month_to);
                  }}
                  valueFrom={watch('from_month') || ''}
                  valueTo={watch('to_month') || ''}
                  hideDropdown
                  style="white"
                />
              </div>
            </form>
          </FormProvider>
        </FiltersPopover>
      )}
    </>
  );
};
